// "Someone Else's Factory": the unassisted review, after JD Pruett's
// pre-onsite code review. Read an unfamiliar system, predict what a change
// breaks, then find the bugs that only show up when people use it.
// Every answer and mark goes through World.apply, so replays match.

import type { ReviewDef } from '../core/levels';
import { clock } from '../core/report';
import { sfx } from '../audio/sfx';
import { clear, h, setText } from '../ui/dom';
import type { Session } from './session';

type Phase =
  | { name: 'read'; until: number }
  | { name: 'predict'; i: number }
  | { name: 'watch'; i: number; until: number }
  | { name: 'explain'; i: number }
  | { name: 'hunt'; until: number }
  | { name: 'chaos'; until: number }
  | { name: 'results' };

export class ReviewController {
  private s: Session;
  private def: ReviewDef;
  private phase: Phase;
  private el: HTMLElement;
  private body: HTMLElement;
  private timer: HTMLElement;
  private choice: string | null = null;

  constructor(s: Session, def: ReviewDef, root: HTMLElement) {
    this.s = s;
    this.def = def;
    this.phase = { name: 'read', until: 0 };
    this.timer = h('span', { class: 'chip' });
    this.body = h('div', { class: 'review-body' });
    this.el = h('aside', { class: 'review panel', 'aria-live': 'polite' }, h('div', { class: 'review-head' }, h('b', { text: 'Review' }), this.timer), this.body);
    root.append(this.el);
  }

  /** Marking is on during the bug hunt: clicks mark instead of select. */
  get marking() {
    return this.phase.name === 'hunt';
  }

  begin() {
    this.phase = { name: 'read', until: this.s.world.seconds + this.def.read.seconds };
    this.s.setSpeed(1);
    this.render();
  }

  private now() {
    return this.s.world.seconds;
  }

  /** Called a few times a second by the session. */
  tick() {
    const p = this.phase;
    const left = 'until' in p ? Math.max(0, p.until - this.now()) : -1;
    setText(this.timer, left >= 0 ? clock(left) : '');
    if (p.name === 'read' && left <= 0) this.lockRead();
    else if (p.name === 'watch' && left <= 0) {
      this.s.setSpeed(0);
      this.phase = { name: 'explain', i: p.i };
      this.render();
    } else if (p.name === 'hunt' && left <= 0) this.endHunt();
    else if (p.name === 'chaos' && left <= 0) {
      this.s.setSpeed(0);
      this.phase = { name: 'results' };
      this.render();
    }
    if (p.name === 'hunt') this.renderMarks();
  }

  private options(opts: string[], onPick: (i: number) => void) {
    return h(
      'div',
      { class: 'options', role: 'radiogroup' },
      opts.map((o, i) =>
        h('button', {
          class: `option${this.choice === String(i) ? ' on' : ''}`, role: 'radio', 'aria-checked': String(this.choice === String(i)),
          onclick: () => {
            this.choice = String(i);
            sfx.play('click', { gain: 0.4 });
            onPick(i);
            this.render();
          },
          text: o,
        }),
      ),
    );
  }

  private lockRead() {
    const w = this.s.world;
    if (this.choice !== null) w.apply({ type: 'answer', q: 'read', choice: this.choice }, 'mouse');
    else w.apply({ type: 'answer', q: 'read', choice: 'none' }, 'mouse');
    this.choice = null;
    this.phase = { name: 'predict', i: 0 };
    this.s.setSpeed(0);
    this.render();
  }

  private lockPredict(i: number) {
    const q = this.def.predict[i];
    const w = this.s.world;
    w.apply({ type: 'answer', q: q.id, choice: this.choice ?? 'none' }, 'mouse');
    w.apply({ type: 'perturb', kind: q.perturb, target: q.target }, 'mouse');
    this.choice = null;
    this.phase = { name: 'watch', i, until: this.now() + q.watch };
    this.s.setSpeed(2);
    this.render();
  }

  private next(i: number) {
    this.s.world.apply({ type: 'calm' }, 'mouse');
    if (i + 1 < this.def.predict.length) {
      this.phase = { name: 'predict', i: i + 1 };
      this.render();
      return;
    }
    this.phase = { name: 'hunt', until: this.now() + this.def.hunt.seconds };
    this.s.setSpeed(1);
    this.s.hud.toast('Click any machine or belt to mark it as a bug. Click again to unmark.', 'info');
    this.render();
  }

  private endHunt() {
    // Show the bugs doing damage: a worse model makes the bypass bleed.
    this.s.world.apply({ type: 'perturb', kind: 'modelUpdate' }, 'mouse');
    this.phase = { name: 'chaos', until: this.now() + 25 };
    this.s.setSpeed(4);
    this.render();
  }

  private finish() {
    this.s.world.apply({ type: 'finishReview' }, 'mouse');
    this.el.remove();
  }

  private renderMarks() {
    const n = this.s.world.marks.size;
    const m = this.body.querySelector('.mark-count');
    if (m) setText(m, `${n} of ${this.def.hunt.maxMarks} marked`);
  }

  private render() {
    const b = this.body;
    clear(b);
    const p = this.phase;
    const w = this.s.world;
    switch (p.name) {
      case 'read':
        b.append(
          h('p', { class: 'step', text: '1 of 3: read it' }),
          h('p', { class: 'q', text: this.def.read.text }),
          h('p', { class: 'hint', text: 'It’s running. Pan around and click machines to inspect them.' }),
          this.options(this.def.read.options, () => undefined),
          h('button', { class: 'btn primary', disabled: this.choice === null, onclick: () => this.lockRead(), text: 'Lock in' }),
        );
        break;
      case 'predict': {
        const q = this.def.predict[p.i];
        b.append(
          h('p', { class: 'step', text: `2 of 3: predict (${p.i + 1} of ${this.def.predict.length})` }),
          h('p', { class: 'q', text: q.text }),
          h('p', { class: 'hint', text: 'The factory is paused. Answer first, then watch it happen.' }),
          this.options(q.options, () => undefined),
          h('button', { class: 'btn primary', disabled: this.choice === null, onclick: () => this.lockPredict(p.i), text: 'Lock in and watch' }),
        );
        break;
      }
      case 'watch':
        b.append(h('p', { class: 'step', text: 'Watching what happens…' }), h('p', { class: 'q', text: this.def.predict[p.i].text }));
        break;
      case 'explain': {
        const q = this.def.predict[p.i];
        const right = w.answers.get(q.id) === q.answer;
        b.append(
          h('p', { class: `verdict ${right ? 'good' : 'bad'}`, text: right ? 'You called it.' : `Not quite. It was: ${q.options[Number(q.answer)]}.` }),
          h('p', { class: 'hint', text: q.explain }),
          h('button', { class: 'btn primary', onclick: () => this.next(p.i), text: p.i + 1 < this.def.predict.length ? 'Next question' : 'Start the bug hunt' }),
        );
        break;
      }
      case 'hunt':
        b.append(
          h('p', { class: 'step', text: '3 of 3: find the bugs' }),
          h('p', { class: 'q', text: 'This factory has bugs that only show up when real people use it. Mark the machines or belts where they live.' }),
          h('p', { class: 'hint', text: 'Think about people doing two things at once, shortcuts someone added, and what happens when something breaks.' }),
          h('p', { class: 'mark-count chip', text: `${w.marks.size} of ${this.def.hunt.maxMarks} marked` }),
          h('button', { class: 'btn primary', onclick: () => this.endHunt(), text: 'Done marking' }),
        );
        break;
      case 'chaos':
        b.append(h('p', { class: 'step', text: 'Running it under real use…' }), h('p', { class: 'hint', text: 'A worse model ships and two teams keep editing the same records.' }));
        break;
      case 'results': {
        const tagOf = new Map<number, string>();
        for (const [tag, id] of w.tags) tagOf.set(id, tag);
        const marked = [...w.marks].map((id) => tagOf.get(id) ?? '');
        b.append(h('p', { class: 'step', text: 'What was wrong' }));
        for (const bug of this.def.bugs) {
          const found = bug.hot.some((t) => marked.includes(t));
          const focus = () => {
            const id = w.tags.get(bug.hot[0]);
            const bb = id ? w.buildings.get(id) : undefined;
            if (bb) this.s.renderer.cam.focus(bb.x + bb.w / 2, bb.y + bb.h / 2);
          };
          b.append(
            h(
              'div',
              { class: `bug ${found ? 'good' : 'bad'}` },
              h('b', { text: `${found ? 'Found' : 'Missed'}: ${bug.name}` }),
              h('span', { text: bug.explain }),
              h('button', { class: 'btn ghost', onclick: focus, text: 'Show me' }),
            ),
          );
        }
        const falseMarks = marked.filter((t) => !this.def.bugs.some((bug) => bug.hot.includes(t))).length;
        if (falseMarks) b.append(h('p', { class: 'hint', text: `${falseMarks} mark${falseMarks === 1 ? ' was' : 's were'} on something that works as intended.` }));
        b.append(h('button', { class: 'btn primary', onclick: () => this.finish(), text: 'See my report' }));
        break;
      }
    }
  }

  destroy() {
    this.el.remove();
  }
}
